import { zodResolver } from '@hookform/resolvers/zod';
import { ActivityLog, Property, User } from '@kalos/kalos-rpc';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Textarea,
  toast,
  useConfirm,
} from '@kalos/ui';
import { format } from 'date-fns';
import { ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { usePropertyUpdateMutation } from '../../../../../hooks/react-query/usePropertiesQuery';
import { useUserUpdateMutation } from '../../../../../hooks/react-query/useUserQuery';
import { ActivityLogClientService } from '../../../../../tools/helpers';
import { getFieldMaskFromDirtyField } from '../../../../ComponentsLibrary/EmployeeDepartments/utils';
import { useSchedulerStyle } from '../../context/schedulerStyle';

/**
 * Notification card. In the scheduler (compact) it collapses to just its header
 * bar; elsewhere it keeps the original always-visible (line-clamped) layout.
 */
const CollapsibleNotification = ({
  label,
  text,
  expanded,
  onToggle,
  onEdit,
  compact,
}: {
  label: string;
  text: string;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  compact: boolean;
}) => {
  if (!compact) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onEdit}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            {text && (
              <button
                type="button"
                onClick={onToggle}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                {expanded ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </button>
            )}
          </div>
        </div>
        {text ? (
          <p
            className={`mt-2 text-sm leading-relaxed text-gray-600 ${
              expanded ? 'whitespace-pre-wrap' : 'line-clamp-3'
            }`}
          >
            {text}
          </p>
        ) : (
          <p className="mt-2 text-sm text-gray-400">No notification set</p>
        )}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between bg-gray-50 px-4 py-2.5">
        <button
          type="button"
          onClick={onToggle}
          className="flex flex-1 items-center text-left text-[10px] font-semibold uppercase tracking-[0.5px] text-gray-400"
        >
          {label}
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onEdit}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onToggle}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            {expanded ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      </div>
      {expanded &&
        (text ? (
          <p className="whitespace-pre-wrap px-4 py-3 text-xs leading-relaxed text-gray-700">
            {text}
          </p>
        ) : (
          <p className="px-4 py-3 text-xs text-gray-400">No notification set</p>
        ))}
    </div>
  );
};

const notificationSchema = z.object({
  notification: z
    .string()
    .min(1, 'Notification is required')
    .max(1000, 'Notification is too long (max 1000 characters)')
    .refine((value) => value.trim() !== '', { message: 'Notification is required' }),
});

type NotificationForm = z.infer<typeof notificationSchema>;

interface ReviewNotificationsProps {
  customer: User;
  property: Property;
  onNotificationSaved: (type: 'customer' | 'property', newText: string) => void;
}

export const ReviewNotifications = ({
  customer,
  property,
  onNotificationSaved,
}: ReviewNotificationsProps) => {
  const compact = useSchedulerStyle();
  const [customerExpanded, setCustomerExpanded] = useState(false);
  const [propertyExpanded, setPropertyExpanded] = useState(false);
  const [editingType, setEditingType] = useState<'customer' | 'property' | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const userMutation = useUserUpdateMutation();
  const propertyMutation = usePropertyUpdateMutation();
  const confirm = useConfirm();

  const customerNotification = customer.notification || '';
  const propertyNotification = property.notification || '';

  const form = useForm<NotificationForm>({
    resolver: zodResolver(notificationSchema),
    defaultValues: { notification: '' },
  });

  useEffect(() => {
    if (editingType === 'customer') {
      form.reset({ notification: customerNotification });
    } else if (editingType === 'property') {
      form.reset({ notification: propertyNotification });
    }
  }, [editingType, customerNotification, propertyNotification, form]);

  const handleCloseEdit = useCallback(
    async (skipConfirm = false) => {
      if (!skipConfirm && form.formState.isDirty) {
        if (!(await confirm('Are you sure you want to leave without saving?'))) return;
      }
      setEditingType(null);
      form.reset();
    },
    [form, confirm],
  );

  const createActivity = useCallback(
    async (message: string, isProperty: boolean) => {
      const actLog = ActivityLog.create();
      actLog.activityName = message;
      actLog.activityDate = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
      if (isProperty) {
        actLog.propertyId = property.id;
      } else {
        actLog.userId = customer.id;
      }
      await ActivityLogClientService.Create(actLog);
    },
    [customer.id, property.id],
  );

  const handleSave = useCallback(
    async (data: NotificationForm) => {
      if (!editingType) return;
      setIsSaving(true);
      try {
        if (editingType === 'customer') {
          await userMutation.mutateAsync(
            User.create({
              id: customer.id,
              notification: data.notification,
              fieldMask: getFieldMaskFromDirtyField(form.formState.dirtyFields),
            }),
          );
          await createActivity(
            customerNotification ? 'Edited notification' : 'Added notification',
            false,
          );
          toast({ variant: 'success', title: 'Customer notification saved' });
          onNotificationSaved('customer', data.notification);
        } else {
          await propertyMutation.mutateAsync(
            Property.create({
              id: property.id,
              notification: data.notification,
              fieldMask: ['Notification'],
            }),
          );
          await createActivity(
            propertyNotification ? 'Edited notification' : 'Added notification',
            true,
          );
          toast({ variant: 'success', title: 'Property notification saved' });
          onNotificationSaved('property', data.notification);
        }
        handleCloseEdit(true);
      } catch (error) {
        toast({ variant: 'destructive', title: 'Failed to save notification' });
        console.error(error);
      } finally {
        setIsSaving(false);
      }
    },
    [
      editingType,
      customer.id,
      property.id,
      customerNotification,
      propertyNotification,
      userMutation,
      propertyMutation,
      form.formState.dirtyFields,
      createActivity,
      onNotificationSaved,
      handleCloseEdit,
    ],
  );

  const handleDelete = useCallback(async () => {
    if (!(await confirm('Are you sure you want to delete this notification?'))) return;
    setIsSaving(true);
    try {
      if (editingType === 'customer') {
        await userMutation.mutateAsync(
          User.create({ id: customer.id, notification: '', fieldMask: ['Notification'] }),
        );
        await createActivity('Deleted notification', false);
        toast({ variant: 'success', title: 'Customer notification deleted' });
        onNotificationSaved('customer', '');
      } else if (editingType === 'property') {
        await propertyMutation.mutateAsync(
          Property.create({ id: property.id, notification: '', fieldMask: ['Notification'] }),
        );
        await createActivity('Deleted notification', true);
        toast({ variant: 'success', title: 'Property notification deleted' });
        onNotificationSaved('property', '');
      }
      handleCloseEdit(true);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to delete notification' });
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  }, [
    editingType,
    customer.id,
    property.id,
    userMutation,
    propertyMutation,
    createActivity,
    onNotificationSaved,
    handleCloseEdit,
    confirm,
  ]);

  const onSubmit = form.handleSubmit(handleSave);

  return (
    <>
      <div className="grid grid-cols-2 items-start gap-3">
        <CollapsibleNotification
          label="Customer Notification"
          text={customerNotification}
          expanded={customerExpanded}
          onToggle={() => setCustomerExpanded(!customerExpanded)}
          onEdit={() => setEditingType('customer')}
          compact={compact}
        />
        <CollapsibleNotification
          label="Property Notification"
          text={propertyNotification}
          expanded={propertyExpanded}
          onToggle={() => setPropertyExpanded(!propertyExpanded)}
          onEdit={() => setEditingType('property')}
          compact={compact}
        />
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editingType} onOpenChange={() => handleCloseEdit()}>
        <DialogContent className="bg-white">
          <DialogTitle>
            {editingType === 'customer' ? 'Customer' : 'Property'} Notification
          </DialogTitle>
          <Form {...form}>
            <form onSubmit={onSubmit} className="space-y-4">
              <FormField
                control={form.control}
                name="notification"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notification</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={6}
                        placeholder="Enter notification..."
                        className="bg-white"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
                <div>
                  {((editingType === 'customer' && customerNotification) ||
                    (editingType === 'property' && propertyNotification)) && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleDelete}
                      disabled={isSaving}
                    >
                      Delete
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleCloseEdit()}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};
